import React from 'react';
import { Target } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Eyebrow, FieldLabel } from '../../components/Primitives.jsx';
import { AuditTable } from './AuditTable.jsx';
import { PValueInput } from './PValueInput.jsx';
import { isCategoricalColumn, resolveLiveRows, mergedColumns } from './columnClassification.js';
import { ColumnsContext, OriginalColumnsContext } from '../StepInspector.jsx';
import { hasUpstreamSplit, SOURCE_ROWS } from '../../data.js';

const NO_CATEGORICAL_MESSAGE =
  'No categorical columns detected. Please ensure categorical features are formatted as text/category or encoded upstream.';

function getFitRows(rows) {
  if (!rows || !rows.length) return [];
  for (const key of Object.keys(rows[0] || {})) {
    const vals = new Set(rows.map(r => r[key]).filter(v => v !== null && v !== ''));
    if (vals.size > 0 && Array.from(vals).every(v => v === 'train' || v === 'test')) {
      const filtered = rows.filter(r => r[key] === 'train');
      if (filtered.length > 0) return filtered;
      break;
    }
  }
  return rows;
}

function gammaLn(z) {
  const p = [
    676.5203681218851, -1259.139216722289, 771.3234287776531,
    -176.6150291621406, 12.507343278686905, -0.13857109526572012,
    9.984369578019572e-6, 1.5056327351493116e-7
  ];
  if (z < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * z)) - gammaLn(1 - z);
  z -= 1;
  let x = 0.9999999999998099;
  for (let i = 0; i < p.length; i++) x += p[i] / (z + i + 1);
  const t = z + p.length - 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x);
}

function incompleteGammaQ(a, x) {
  if (x <= 0 || a <= 0) return 1.0;
  if (x < a + 1.0) {
    let ap = a;
    let sum = 1.0 / a;
    let del = sum;
    for (let n = 1; n <= 100; n++) {
      ap += 1.0;
      del *= x / ap;
      sum += del;
      if (Math.abs(del) < Math.abs(sum) * 1e-12) break;
    }
    const p = sum * Math.exp(-x + a * Math.log(x) - gammaLn(a));
    return Math.max(0.0, 1.0 - p);
  } else {
    let b = x + 1.0 - a;
    let c = 1.0 / 1e-30;
    let d = 1.0 / b;
    let h = d;
    for (let i = 1; i <= 100; i++) {
      const an = -i * (i - a);
      b += 2.0;
      d = an * d + b;
      if (Math.abs(d) < 1e-30) d = 1e-30;
      c = b + an / c;
      if (Math.abs(c) < 1e-30) c = 1e-30;
      d = 1.0 / d;
      const del = d * c;
      h *= del;
      if (Math.abs(del - 1.0) < 1e-12) break;
    }
    return Math.max(0.0, h * Math.exp(-x + a * Math.log(x) - gammaLn(a)));
  }
}

function chi2PValue(chi2, df) {
  if (df <= 0 || chi2 <= 0) return 1.0;
  return incompleteGammaQ(df / 2.0, chi2 / 2.0);
}

function computeChi2Scores(rows, targetCol, maxPValue, manualOverrides = {}, columnsList = []) {
  if (!rows || !rows.length || !targetCol) return [];
  const fitRows = getFitRows(rows);
  const typeOf = (name) => (columnsList.find(c => c.name === name)?.type) || 'string';

  // Candidate features: genuinely categorical/discrete columns only (by type
  // and cardinality, not a name-substring guess) — excludes targetCol and
  // __rowIndex. Deliberately does NOT fall back to "every column" when none
  // qualify; that fallback used to silently treat continuous numeric columns
  // as categorical, hiding the real "nothing to test" condition.
  const evalCols = columnsList
    .filter(c => c.name !== targetCol && c.name !== '__rowIndex' && isCategoricalColumn(c, fitRows))
    .map(c => c.name);

  return evalCols.map(col => {
    // Build contingency table
    const freqMap = {};
    const rowSums = {};
    const colSums = {};
    let totalN = 0;

    for (const r of fitRows) {
      const x = r[col];
      const y = r[targetCol];
      if (x === null || x === undefined || x === '' || y === null || y === undefined || y === '') continue;

      const sx = String(x);
      const sy = String(y);

      freqMap[sx] = freqMap[sx] || {};
      freqMap[sx][sy] = (freqMap[sx][sy] || 0) + 1;

      rowSums[sx] = (rowSums[sx] || 0) + 1;
      colSums[sy] = (colSums[sy] || 0) + 1;
      totalN += 1;
    }

    const rowKeys = Object.keys(rowSums);
    const colKeys = Object.keys(colSums);

    if (rowKeys.length < 2 || colKeys.length < 2 || totalN === 0) {
      return {
        column: col,
        dataType: typeOf(col),
        chi2Score: null,
        pValue: 1.0,
        metric: 'N/A (constant / no overlap)',
        autoStatus: 'drop',
        override: manualOverrides[col] || 'auto'
      };
    }

    let chi2 = 0.0;
    for (const r of rowKeys) {
      for (const cKey of colKeys) {
        const obs = freqMap[r]?.[cKey] || 0;
        const exp = (rowSums[r] * colSums[cKey]) / totalN;
        if (exp > 0) {
          chi2 += Math.pow(obs - exp, 2) / exp;
        }
      }
    }

    const df = (rowKeys.length - 1) * (colKeys.length - 1);
    const pValue = chi2PValue(chi2, df);

    const autoStatus = pValue <= maxPValue ? 'keep' : 'drop';
    const displayPValue = pValue < 0.0001 ? '< 0.0001' : pValue.toFixed(4);

    return {
      column: col,
      dataType: typeOf(col),
      chi2Score: chi2,
      pValue,
      metric: `χ²=${chi2.toFixed(4)}, p=${displayPValue}`,
      autoStatus,
      override: manualOverrides[col] || 'auto'
    };
  });
}

export function Chi2Inspector({ step, onChange, pipeline, uploadedRows }) {
  const c = step.config || {};
  const targetCol = c.target || '';
  const maxPValue = c.max_p_value ?? 0.05;
  const manualOverrides = c.manual_overrides || {};

  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const splitReady = pipeline ? hasUpstreamSplit(pipeline.steps, step.id) : true;
  const rows = resolveLiveRows(uploadedRows, pipeline, SOURCE_ROWS);

  const liveColumns = React.useContext(ColumnsContext) || [];
  const origColumns = React.useContext(OriginalColumnsContext) || [];
  const allCols = mergedColumns(origColumns, liveColumns);

  // Filter target options to genuinely Categorical / Discrete columns (type
  // + cardinality based — see columnClassification.js), falling back to
  // every column only when the dataset has none at all to offer.
  const targetCols = React.useMemo(() => {
    const cats = allCols.filter(c => isCategoricalColumn(c, rows));
    return cats.length ? cats : allCols;
  }, [allCols, rows]);

  const scores = computeChi2Scores(rows, targetCol, maxPValue, manualOverrides, allCols);
  const noCategoricalCandidates = Boolean(targetCol) && scores.length === 0;

  const setOverride = (col, next) => {
    const nextOverrides = { ...manualOverrides };
    if (next === 'auto') {
      delete nextOverrides[col];
    } else {
      nextOverrides[col] = next;
    }
    u({ manual_overrides: nextOverrides });
  };

  return (
    <div>
      <Eyebrow>Chi-Square Selection</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Evaluate categorical/discrete features against a categorical target using the Chi-Square ($\chi^2$) test of independence.
      </div>

      {!splitReady && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          No Train / Test Split step found upstream — Chi-Square statistics are calculated using all rows. Add a Train / Test Split step first to avoid data leakage.
        </Alert>
      )}

      {/* Target Variable Dropdown */}
      <div style={{ marginBottom: 12 }}>
        <FieldLabel hint="Select a categorical/discrete target variable to test feature independence against">
          Target Variable (Categorical / Discrete)
        </FieldLabel>
        <select
          value={targetCol}
          onChange={(e) => u({ target: e.target.value })}
          style={{
            width: '100%', background: C.input, color: C.fg1,
            border: `1px solid ${C.borderStrong}`, borderRadius: 6,
            padding: '6px 9px', fontSize: '.82rem', outline: 'none',
            fontFamily: 'inherit', marginTop: 4, cursor: 'pointer'
          }}
        >
          <option value="" disabled>Select target column…</option>
          {targetCols.map(col => (
            <option key={col.name} value={col.name}>
              {col.name} ({col.type || 'string'})
            </option>
          ))}
        </select>
      </div>

      {/* Significance Presets */}
      <div style={{ marginBottom: 12 }}>
        <FieldLabel hint="Maximum p-value threshold for feature retention">Significance Presets</FieldLabel>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: 'Standard (p <= 0.05)', val: 0.05 },
            { label: 'Strict (p <= 0.01)', val: 0.01 },
            { label: 'Relaxed (p <= 0.10)', val: 0.10 },
          ].map(p => {
            const active = maxPValue === p.val;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => u({ max_p_value: p.val })}
                style={{
                  background: active ? C.accentStrong : C.input,
                  color: active ? '#fff' : C.fg3,
                  border: `1px solid ${active ? C.accentStrong : C.borderStrong}`,
                  borderRadius: 6, padding: '4px 8px', fontSize: '.74rem',
                  fontWeight: 600, cursor: 'pointer', transition: 'all .15s'
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Max p-value input */}
      <div style={{ marginBottom: 14 }}>
        <FieldLabel hint="Maximum p-value (features with p-value > max_p_value are dropped)">
          Max p-value threshold: {maxPValue}
        </FieldLabel>
        <PValueInput
          value={maxPValue}
          onChange={(n) => u({ max_p_value: n })}
        />
      </div>

      {/* Live Preview Table */}
      {targetCol ? (
        noCategoricalCandidates ? (
          <Alert kind="warn" style={{ marginTop: 10 }}>{NO_CATEGORICAL_MESSAGE}</Alert>
        ) : (
          <AuditTable
            headerLabel={<><Target size={12} style={{ display: 'inline', verticalAlign: -1, marginRight: 5 }} />Evaluating features against Target: {targetCol}</>}
            rows={scores.map(row => ({
              column: row.column,
              dataType: row.dataType,
              metric: row.metric,
              autoStatus: row.autoStatus,
              override: row.override,
              onOverrideChange: (next) => setOverride(row.column, next),
            }))}
            emptyMessage={NO_CATEGORICAL_MESSAGE}
          />
        )
      ) : (
        <div style={{
          marginTop: 10, padding: 12, borderRadius: 8, background: C.surface,
          border: `1px dashed ${C.border}`, color: C.fg4, fontSize: '.76rem', textAlign: 'center'
        }}>
          Please select a categorical target variable above to display live Chi-Square scores.
        </div>
      )}
    </div>
  );
}

